import { validateTokenForWallet } from "@src/actions/auth"
import type { SessionResult } from "@src/types/session"
import type { QueryClient } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

export interface UseWalletAuthResult {
  isAuthorized: boolean
  isSessionExpired: boolean
  isDegraded: boolean
  isValidating: boolean
  accountId: string | null
  sessionResult: SessionResult | null
}

export function syncWalletAuthCache(
  queryClient: QueryClient,
  address: string | null | undefined,
  chainType: string | null | undefined,
  sessionResult: SessionResult | null | undefined
) {
  if (!address || !chainType || !sessionResult) return

  queryClient.setQueryData(
    ["token_validation", address, chainType],
    sessionResult
  )
}

/**
 * Hook that manages wallet authentication state via server-side JWT validation.
 * The JWT is stored in an httpOnly cookie and validated server-side.
 */
export function useWalletAuth(
  address: string | undefined,
  chainType: string | undefined
): UseWalletAuthResult {
  const tokenValidation = useQuery({
    queryKey: ["token_validation", address, chainType],
    queryFn: async (): Promise<SessionResult> => {
      if (!address || !chainType) {
        return { status: "unauthenticated" }
      }
      return validateTokenForWallet(address, chainType)
    },
    enabled: address != null && chainType != null,
    staleTime: 30_000,
    retry: false,
  })

  const result = tokenValidation.data
  const isAuthorized = result?.status === "authorized"
  const isSessionExpired = result?.status === "reauth_required"
  const isDegraded = result?.status === "retryable_failure"
  const isValidating = tokenValidation.isPending
  const accountId = result?.status === "authorized" ? result.accountId : null

  return {
    isAuthorized,
    isSessionExpired,
    isDegraded,
    isValidating,
    accountId,
    sessionResult: result ?? null,
  }
}
